import { defaultTheme } from '@dataplace.ai/ui-components/themes/defaultTheme'
import { useTranslation } from 'react-i18next'
import { AgeBucket } from '../OverviewDemography'

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useDemographyChart = (ageBuckets: AgeBucket[]) => {
  const { palette } = defaultTheme
  const { t } = useTranslation()

  const labels = ageBuckets.map(({ ageRange }) => ageRange)

  const {
    backgroundData, maleData, femaleData,
  } = ageBuckets.reduce((acc, {
    share, male, female,
  }) => {
    acc.backgroundData.push(100 - share)
    acc.maleData.push((share * male.share) / 100)
    acc.femaleData.push((share * female.share) / 100)
    return acc
  }, {
    backgroundData: [],
    maleData: [],
    femaleData: [],
  } as Record<string, number[]>)

  const data = {
    labels,
    datasets: [
      {
        label: 'Background',
        data: backgroundData,
        backgroundColor: palette.ghostWhite,
        hoverBackgroundColor: palette.ghostWhite,
        borderColor: palette.ghostWhite,
        hoverBorderColor: palette.ghostWhite,
        borderWidth: 0,
        stack: 'data',
        order: 3,
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'right',
          formatter: (_: number, ctx: { dataIndex: number }) =>
            `${ageBuckets[ctx.dataIndex].share.toFixed(1)}%`,
        },
      },
      {
        label: t('generic.men'),
        data: maleData,
        backgroundColor: palette.electricBlue,
        stack: 'data',
        order: 1,
      },
      {
        label: t('generic.women'),
        data: femaleData,
        backgroundColor: palette.pastelPurple,
        stack: 'data',
        order: 2,
      },
    ],
  }

  const options = {
    plugins: {
      datalabels: {
        display: false,
        font: {
          size: 10,
        },
      },
    },
    tooltips: {
      enabled: true,
      mode: 'nearest',
      intersect: true,
      backgroundColor: palette.dark.main,
      titleFontColor: palette.light.white,
      bodyFontColor: palette.light.white,
      footerFontColor: palette.light.white,
      filter(item: { datasetIndex?: number}) {
        return item.datasetIndex !== 0
      },
      callbacks: {
        title: () => null,
        label: (tooltipItem: { index: number; datasetIndex?: number }) => {
          const bucket = ageBuckets[tooltipItem.index]

          if (tooltipItem.datasetIndex === 1) return `${t('generic.men')}: ${bucket.male.share.toFixed(1)}%`
          if (tooltipItem.datasetIndex === 2) return `${t('generic.women')}: ${bucket.female.share.toFixed(1)}%`
          return null
        },
      },
    },
    scales: {
      xAxes: [
        {
          stacked: true,
          display: false,
          ticks: {
            beginAtZero: true,
            max: 100,
          },
          gridLines: {
            display: false,
            drawBorder: false,
          },
          barPercentage: 1.0,
          categoryPercentage: 0.6,
        },
      ],
      yAxes: [
        {
          stacked: true,
          gridLines: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            fontColor: palette.dark.raven,
            fontSize: 10,
          },
          barThickness: 10,
        },
      ],
    },
    legend: {
      position: 'top',
      align: 'start',
      labels: {
        usePointStyle: true,
        fontColor: palette.black,
        padding: 10,
        fontSize: 10,
        filter: (legendItem: { datasetIndex?: number }) =>
          legendItem.datasetIndex !== 0,
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        right: 40,
      },
    },
  }

  return {
    data,
    options,
  }
}
